import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useParams, useNavigate, useBlocker } from 'react-router-dom';
import {
  Grid,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Box,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tabs,
  Tab,
  Tooltip,
  Chip,
  CircularProgress,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  ArrowBack,
  Edit,
  Save,
  Cancel,
  Add,
  Delete,
  WarningAmber,
} from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';
import {
  useApiGet,
  useApiSend,
  getApiErrorMessage,
} from '../../utils/httpClientTs';
import {
  getFundDetail,
  getFundHistory,
  patchFund,
  createFundNote,
  patchFundNote,
  deleteFundNote,
  createFundPosition,
  replaceFundPosition,
  patchFundPosition,
  deleteFundPosition,
  getFundChairOtherFunds,
  createFundChairRoleSlot,
  patchFundChairRoleSlot,
  deleteFundChairRoleSlot,
  createFundEmailContact,
  patchFundEmailContact,
  deleteFundEmailContact,
  createFundEmailRoleSlot,
  patchFundEmailRoleSlot,
  deleteFundEmailRoleSlot,
} from '../../endpoints/financeFunds';
import { getSPDChaptersList } from '../../endpoints/chaptersTs';
import type { SPDChapterListResponse } from '../../types/chapters';
import ChapterSelector from '../../Components/ChapterSelector';
import {
  FUND_CATEGORY_TYPE_ALLOCATION_ID,
  fundTypeRequiresCustomName,
  fundTypeAllowsMultipleRows,
  fundSubtypeRequiresNamedLimit,
  fundSubtypeIsSpecialRecognition,
  FUND_OPTION_B,
  FUND_OPTION_CUSTOM,
  FUND_OPTION_LABELS,
  fundOptionLabel,
  TRANSFER_ALLOCATION_MODE_NORMAL,
  TRANSFER_ALLOCATION_MODE_ORDER,
} from '../../types/financeFunds';
import type {
  FundDetailResponse,
  FundHistoryItem,
  FundHistoryResponse,
  PatchFundRequest,
  PatchFundPositionRequest,
  ReplaceFundPositionRequest,
  FundChairOtherFundDTO,
  CreateFundRoleSlotRequest,
  PatchFundRoleSlotRequest,
  CreateFundEmailContactRequest,
  PatchFundEmailContactRequest,
  FundAssignedTypeInput,
  TransferAllocationMode,
  PatchFundOptionInput,
  FundOptionCode,
} from '../../types/financeFunds';
import PositionDisplay from '../../Components/PositionDisplay';
import FundRoleSlotDisplay from '../../Components/FundRoleSlotDisplay';
import AlertItem from '../../Components/AlertItem';
import FundAttachmentsTab from './FundAttachmentsTab';
import FundStatementDataTab from './FundStatementDataTab';
import FundNamedAmountsSection from './FundNamedAmountsSection';
import FundTypeAllocationEditor, {
  AssignedTypeRow,
} from './FundTypeAllocationEditor';

const FUND_STATEMENT_TAB = 'statement';
const FUND_HISTORY_TAB = 'history';

type FundDetailTab =
  | 'information'
  | 'positions'
  | 'notes'
  | 'attachments'
  | 'statement'
  | 'history';

interface PositionSaveData {
  id: string;
  positionId?: string;
  committeeId?: string;
  action: 'newAppointment' | 'editDates' | 'removeRecord';
  userId?: string;
  incumbentUserId?: string;
  startDate?: string | null;
  endDate?: string | null;
}

const formatAllocationAmount = (amount: number) =>
  amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fundHistoryKindLabel = (kind: FundHistoryItem['kind']): string => {
  switch (kind) {
    case 'field_change':
      return 'Field';
    case 'fund_option':
      return 'Option';
    case 'named_amount':
      return 'Named amount';
    default:
      return kind;
  }
};

const fundHistoryDisplayValue = (label?: string, value?: string): string => {
  if (label != null && label !== '') return label;
  if (value != null && value !== '') return value;
  return '(cleared)';
};

const fundHistoryRangeLabel = (start?: string, end?: string | null): string => {
  const startLabel = start || '—';
  if (end == null || end === '') {
    return `${startLabel} – Present`;
  }
  return `${startLabel} – ${end}`;
};
const namedLimitPattern = /^(?:[1-9]|10)$/;

const normStr = (v?: string | null) => (v ?? '').trim();

const assignedTypesSignature = (rows: AssignedTypeRow[]) =>
  JSON.stringify(
    rows.map((r) => ({
      type_id: normStr(r.type_id),
      subtype_id: normStr(r.subtype_id),
      percentage: normStr(r.percentage),
      other_transfer_name: normStr(r.other_transfer_name),
      amount: normStr(r.amount),
      named_limit: normStr(r.named_limit),
      special_recognition_count: normStr(r.special_recognition_count),
    }))
  );

const FundDetail: React.FC = () => {
  const { id: fundId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [editingFund, setEditingFund] = useState(false);
  const [name, setName] = useState('');
  const [shortName, setShortName] = useState('');
  const [accountId, setAccountId] = useState('');
  const [description, setDescription] = useState('');
  const [canDonate, setCanDonate] = useState(false);
  const [typeId, setTypeId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [subtypeId, setSubtypeId] = useState('');
  const [statusId, setStatusId] = useState('');
  const [detailTab, setDetailTab] = useState<FundDetailTab>('information');
  const [spdChapterId, setSpdChapterId] = useState('');
  const [localChapterId, setLocalChapterId] = useState('');
  const [establishedBy, setEstablishedBy] = useState('');
  const [establishedDate, setEstablishedDate] = useState<Dayjs | null>(null);
  const [asOfDate, setAsOfDate] = useState<Dayjs>(() => dayjs());
  const [optOut, setOptOut] = useState(false);
  const [selectionCommittee, setSelectionCommittee] = useState('');
  const [transferLetter, setTransferLetter] = useState('');
  const [statementParagraph, setStatementParagraph] = useState('');
  const [activityLetter, setActivityLetter] = useState('');
  const [fundUse, setFundUse] = useState('');
  const [principalAmount, setPrincipalAmount] = useState('');
  const [distributionOption, setDistributionOption] = useState('');
  const [optionDescription, setOptionDescription] = useState('');
  const [yearlyAllocation, setYearlyAllocation] = useState('');
  const [assignedTypes, setAssignedTypes] = useState<AssignedTypeRow[]>([]);
  const [transferAllocationMode, setTransferAllocationMode] =
    useState<TransferAllocationMode>(TRANSFER_ALLOCATION_MODE_NORMAL);
  const [optionDirty, setOptionDirty] = useState(false);
  const [unsavedDialogOpen, setUnsavedDialogOpen] = useState(false);
  const [pendingDetailTab, setPendingDetailTab] =
    useState<FundDetailTab | null>(null);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [pendingLeavePath, setPendingLeavePath] = useState<string | null>(null);
  const allowNavigationRef = useRef(false);

  const [newNote, setNewNote] = useState('');
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [editNoteId, setEditNoteId] = useState<string | null>(null);
  const [editNoteText, setEditNoteText] = useState('');

  const [fundError, setFundError] = useState<string | null>(null);
  const [noteError, setNoteError] = useState<string | null>(null);
  const [positionSaving, setPositionSaving] = useState(false);
  const [positionError, setPositionError] = useState<string | null>(null);
  const [chairRoleSlotSaving, setChairRoleSlotSaving] = useState(false);
  const [chairRoleSlotError, setChairRoleSlotError] = useState<string | null>(
    null
  );
  const [emailRoleSlotSaving, setEmailRoleSlotSaving] = useState(false);
  const [emailRoleSlotError, setEmailRoleSlotError] = useState<string | null>(
    null
  );
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [chairConflict, setChairConflict] = useState<{
    funds: FundChairOtherFundDTO[];
    pending: {
      userId: string;
      roleId: string;
      startDate: string;
      endDate?: string | null;
      memberId?: string;
    };
  } | null>(null);

  const {
    data,
    isLoading,
    isError,
    error: loadError,
  } = useApiGet<FundDetailResponse, string>(
    ['fund-detail', fundId!],
    getFundDetail,
    { enabled: !!fundId, retry: 1 },
    fundId!
  );

  const {
    data: historyData,
    isLoading: historyLoading,
    isError: historyIsError,
    error: historyError,
  } = useApiGet<FundHistoryResponse, string>(
    ['fund-history', fundId!],
    getFundHistory,
    {
      enabled: !!fundId && detailTab === FUND_HISTORY_TAB,
      retry: 1,
    },
    fundId!
  );

  const syncFromData = useCallback((d: FundDetailResponse) => {
    setName(d.name || '');
    setShortName(d.short_name || '');
    setAccountId(d.account_id || '');
    setDescription(d.description || '');
    setCanDonate(d.can_donate === 1);
    setTypeId(d.type_id || '');
    setCategoryId(d.category_id || '');
    setSubtypeId(d.subtype_id || '');
    setStatusId(d.status_id || '');
    setSpdChapterId(d.spd_chapter_id || '');
    setLocalChapterId(d.local_chapter_id || '');
    setEstablishedBy(d.established_by || '');
    setEstablishedDate(d.established_date ? dayjs(d.established_date) : null);
    setAsOfDate(dayjs());
    setOptOut(d.opt_out === 1);
    setSelectionCommittee(d.selection_committee || '');
    setTransferLetter(d.transfer_letter || '');
    setStatementParagraph(d.statement_paragraph || '');
    setActivityLetter(d.activity_letter || '');
    setFundUse(d.fund_use || '');
    setPrincipalAmount(
      d.principal_amount != null ? String(d.principal_amount) : ''
    );
    const cur = d.current_fund_option;
    setDistributionOption(cur?.option || '');
    setOptionDescription(cur?.description || '');
    setYearlyAllocation(
      cur?.yearly_allocation != null ? String(cur.yearly_allocation) : ''
    );
    setOptionDirty(false);
    if (d.uses_type_allocations) {
      setTransferAllocationMode(
        d.transfer_allocation_mode === TRANSFER_ALLOCATION_MODE_ORDER
          ? TRANSFER_ALLOCATION_MODE_ORDER
          : TRANSFER_ALLOCATION_MODE_NORMAL
      );
      if (d.assigned_types && d.assigned_types.length > 0) {
        setAssignedTypes(
          d.assigned_types.map((row) => ({
            type_id: row.type_id,
            subtype_id: row.subtype_id || '',
            percentage:
              row.percentage != null && row.percentage > 0
                ? String(row.percentage)
                : '',
            other_transfer_name: row.other_transfer_name || '',
            amount: row.amount != null ? String(row.amount) : '',
            named_limit: row.named_limit != null ? String(row.named_limit) : '',
            special_recognition_count: fundSubtypeIsSpecialRecognition(
              row.subtype_id || ''
            )
              ? String(
                  row.special_recognition_count != null &&
                    row.special_recognition_count > 0
                    ? row.special_recognition_count
                    : 1
                )
              : '',
          }))
        );
      } else if (d.type_id) {
        setAssignedTypes([
          {
            type_id: d.type_id,
            subtype_id: d.subtype_id || '',
            percentage: '100',
            other_transfer_name: '',
            amount: '',
            named_limit: '',
            special_recognition_count: '',
          },
        ]);
      } else {
        setAssignedTypes([
          {
            type_id: '',
            subtype_id: '',
            percentage: '100',
            other_transfer_name: '',
            amount: '',
            named_limit: '',
            special_recognition_count: '',
          },
        ]);
      }
    } else {
      setAssignedTypes([]);
      setTransferAllocationMode(TRANSFER_ALLOCATION_MODE_NORMAL);
    }
  }, []);

  useEffect(() => {
    if (data && !editingFund) {
      syncFromData(data);
    }
  }, [data, editingFund, syncFromData]);

  const isFundFormDirty = useMemo(() => {
    if (!editingFund || !data) return false;
    if (optionDirty) return true;

    const baselinePrincipal =
      data.principal_amount != null ? String(data.principal_amount) : '';
    const baselineEstablished = data.established_date
      ? dayjs(data.established_date).format('YYYY-MM-DD')
      : '';
    const currentEstablished = establishedDate?.isValid()
      ? establishedDate.format('YYYY-MM-DD')
      : '';

    if (normStr(name) !== normStr(data.name)) return true;
    if (normStr(shortName) !== normStr(data.short_name)) return true;
    if (normStr(accountId) !== normStr(data.account_id)) return true;
    if (normStr(description) !== normStr(data.description)) return true;
    if (canDonate !== (data.can_donate === 1)) return true;
    if (normStr(typeId) !== normStr(data.type_id)) return true;
    if (normStr(categoryId) !== normStr(data.category_id)) return true;
    if (normStr(subtypeId) !== normStr(data.subtype_id)) return true;
    if (normStr(statusId) !== normStr(data.status_id)) return true;
    if (normStr(spdChapterId) !== normStr(data.spd_chapter_id)) return true;
    if (normStr(localChapterId) !== normStr(data.local_chapter_id)) return true;
    if (normStr(establishedBy) !== normStr(data.established_by)) return true;
    if (currentEstablished !== baselineEstablished) return true;
    if (optOut !== (data.opt_out === 1)) return true;
    if (normStr(selectionCommittee) !== normStr(data.selection_committee))
      return true;
    if (normStr(transferLetter) !== normStr(data.transfer_letter)) return true;
    if (normStr(statementParagraph) !== normStr(data.statement_paragraph))
      return true;
    if (normStr(activityLetter) !== normStr(data.activity_letter)) return true;
    if (normStr(fundUse) !== normStr(data.fund_use)) return true;
    if (normStr(principalAmount) !== normStr(baselinePrincipal)) return true;

    const cur = data.current_fund_option;
    if (normStr(distributionOption) !== normStr(cur?.option)) return true;
    if (normStr(optionDescription) !== normStr(cur?.description)) return true;
    const baselineYearly =
      cur?.yearly_allocation != null ? String(cur.yearly_allocation) : '';
    if (normStr(yearlyAllocation) !== normStr(baselineYearly)) return true;

    if (data.uses_type_allocations) {
      const baselineMode =
        data.transfer_allocation_mode === TRANSFER_ALLOCATION_MODE_ORDER
          ? TRANSFER_ALLOCATION_MODE_ORDER
          : TRANSFER_ALLOCATION_MODE_NORMAL;
      if (transferAllocationMode !== baselineMode) return true;
      const baselineRows: AssignedTypeRow[] =
        data.assigned_types && data.assigned_types.length > 0
          ? data.assigned_types.map((row) => ({
              type_id: row.type_id,
              subtype_id: row.subtype_id || '',
              percentage:
                row.percentage != null && row.percentage > 0
                  ? String(row.percentage)
                  : '',
              other_transfer_name: row.other_transfer_name || '',
              amount: row.amount != null ? String(row.amount) : '',
              named_limit:
                row.named_limit != null ? String(row.named_limit) : '',
              special_recognition_count: fundSubtypeIsSpecialRecognition(
                row.subtype_id || ''
              )
                ? String(
                    row.special_recognition_count != null &&
                      row.special_recognition_count > 0
                      ? row.special_recognition_count
                      : 1
                  )
                : '',
            }))
          : data.type_id
            ? [
                {
                  type_id: data.type_id,
                  subtype_id: data.subtype_id || '',
                  percentage: '100',
                  other_transfer_name: '',
                  amount: '',
                  named_limit: '',
                  special_recognition_count: '',
                },
              ]
            : [
                {
                  type_id: '',
                  subtype_id: '',
                  percentage: '100',
                  other_transfer_name: '',
                  amount: '',
                  named_limit: '',
                  special_recognition_count: '',
                },
              ];
      if (
        assignedTypesSignature(assignedTypes) !==
        assignedTypesSignature(baselineRows)
      ) {
        return true;
      }
    }

    return false;
  }, [
    editingFund,
    data,
    optionDirty,
    name,
    shortName,
    accountId,
    description,
    canDonate,
    typeId,
    categoryId,
    subtypeId,
    statusId,
    spdChapterId,
    localChapterId,
    establishedBy,
    establishedDate,
    optOut,
    selectionCommittee,
    transferLetter,
    statementParagraph,
    activityLetter,
    fundUse,
    principalAmount,
    distributionOption,
    optionDescription,
    yearlyAllocation,
    assignedTypes,
    transferAllocationMode,
  ]);

  const hasUnsavedFundEdits = editingFund && isFundFormDirty;

  const discardFundEdits = useCallback(() => {
    if (data) syncFromData(data);
    setEditingFund(false);
    setFundError(null);
  }, [data, syncFromData]);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (allowNavigationRef.current) return false;
    if (currentLocation.pathname === nextLocation.pathname) return false;
    return hasUnsavedFundEdits;
  });

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setLeaveDialogOpen(true);
    }
  }, [blocker.state]);

  useEffect(() => {
    if (!hasUnsavedFundEdits) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [hasUnsavedFundEdits]);

  const requestDetailTabChange = (next: FundDetailTab) => {
    if (next === detailTab) return;
    if (!hasUnsavedFundEdits) {
      setDetailTab(next);
      return;
    }
    setPendingDetailTab(next);
    setUnsavedDialogOpen(true);
  };

  const requestNavigateAway = (path: string) => {
    if (!hasUnsavedFundEdits) {
      navigate(path);
      return;
    }
    setPendingLeavePath(path);
    setLeaveDialogOpen(true);
  };

  const handleUnsavedTabStay = () => {
    setPendingDetailTab(null);
    setUnsavedDialogOpen(false);
  };

  const handleUnsavedTabDiscard = () => {
    const next = pendingDetailTab;
    discardFundEdits();
    setPendingDetailTab(null);
    setUnsavedDialogOpen(false);
    if (next) setDetailTab(next);
  };

  const handleLeaveStay = () => {
    setPendingLeavePath(null);
    setLeaveDialogOpen(false);
    if (blocker.state === 'blocked') {
      blocker.reset?.();
    }
  };

  const handleLeaveDiscard = () => {
    const path = pendingLeavePath;
    discardFundEdits();
    setPendingLeavePath(null);
    setLeaveDialogOpen(false);
    allowNavigationRef.current = true;
    if (blocker.state === 'blocked') {
      blocker.proceed?.();
    } else if (path) {
      navigate(path);
    }
    allowNavigationRef.current = false;
  };
  const { data: spdChapters = [] } = useApiGet<SPDChapterListResponse[]>(
    ['fund-detail-spd-chapters'],
    getSPDChaptersList,
    { enabled: !!fundId, staleTime: 60_000 }
  );

  const spdLabel = (sp: SPDChapterListResponse) => {
    const abbr = sp.abbreviation?.trim();
    return abbr ? `${sp.name} (${abbr})` : sp.name;
  };

  const { mutate: patchFundMut } = useApiSend(
    (p: { id: string; body: PatchFundRequest }) => patchFund(p.id, p.body),
    () => {
      setEditingFund(false);
      setFundError(null);
    },
    (e: Error) => setFundError(getApiErrorMessage(e)),
    [
      ['fund-detail', fundId],
      ['fund-history', fundId],
    ]
  );

  const { mutate: addNoteMut } = useApiSend(
    (p: { id: string; note: string }) => createFundNote(p.id, { note: p.note }),
    () => {
      setNewNote('');
      setNoteError(null);
    },
    (e: Error) => setNoteError(getApiErrorMessage(e)),
    [['fund-detail', fundId]]
  );

  const { mutate: patchNoteMut } = useApiSend(
    (p: { id: string; noteId: string; note: string }) =>
      patchFundNote(p.id, p.noteId, { note: p.note }),
    () => {
      setNoteDialogOpen(false);
      setEditNoteId(null);
      setNoteError(null);
    },
    (e: Error) => setNoteError(getApiErrorMessage(e)),
    [['fund-detail', fundId]]
  );

  const { mutate: deleteNoteMut } = useApiSend(
    (p: { id: string; noteId: string }) => deleteFundNote(p.id, p.noteId),
    () => {
      setNoteError(null);
    },
    (e: Error) => setNoteError(getApiErrorMessage(e)),
    [['fund-detail', fundId]]
  );

  const { mutate: addPositionMut } = useApiSend(
    (p: {
      id: string;
      body: {
        user_id: string;
        role_id: string;
        start_date: string;
        end_date?: string | null;
        apply_to_other_funds?: boolean;
      };
    }) => createFundPosition(p.id, p.body),
    () => {
      setPositionSaving(false);
      setPositionError(null);
    },
    (e: Error) => {
      setPositionSaving(false);
      setPositionError(getApiErrorMessage(e));
    },
    [['fund-detail', fundId]]
  );

  const { mutate: replacePositionMut } = useApiSend(
    (p: { id: string; memberId: string; body: ReplaceFundPositionRequest }) =>
      replaceFundPosition(p.id, p.memberId, p.body),
    () => {
      setPositionSaving(false);
      setPositionError(null);
    },
    (e: Error) => {
      setPositionSaving(false);
      setPositionError(getApiErrorMessage(e));
    },
    [['fund-detail', fundId]]
  );

  const { mutate: patchPositionMut } = useApiSend(
    (p: { id: string; memberId: string; body: PatchFundPositionRequest }) =>
      patchFundPosition(p.id, p.memberId, p.body),
    () => {
      setPositionSaving(false);
      setPositionError(null);
    },
    (e: Error) => {
      setPositionSaving(false);
      setPositionError(getApiErrorMessage(e));
    },
    [['fund-detail', fundId]]
  );

  const { mutate: deletePositionMut } = useApiSend(
    (p: { id: string; memberId: string }) =>
      deleteFundPosition(p.id, p.memberId),
    () => {
      setPositionSaving(false);
      setPositionError(null);
    },
    (e: Error) => {
      setPositionSaving(false);
      setPositionError(getApiErrorMessage(e));
    },
    [['fund-detail', fundId]]
  );

  const { mutateAsync: createChairRoleSlotMut } = useApiSend(
    (p: { id: string; body: CreateFundRoleSlotRequest }) =>
      createFundChairRoleSlot(p.id, p.body),
    () => {
      setChairRoleSlotSaving(false);
      setChairRoleSlotError(null);
    },
    (e: Error) => {
      setChairRoleSlotSaving(false);
      setChairRoleSlotError(getApiErrorMessage(e));
    },
    [['fund-detail', fundId]]
  );

  const { mutateAsync: patchChairRoleSlotMut } = useApiSend(
    (p: { id: string; slotId: string; body: PatchFundRoleSlotRequest }) =>
      patchFundChairRoleSlot(p.id, p.slotId, p.body),
    () => {
      setChairRoleSlotSaving(false);
      setChairRoleSlotError(null);
    },
    (e: Error) => {
      setChairRoleSlotSaving(false);
      setChairRoleSlotError(getApiErrorMessage(e));
    },
    [['fund-detail', fundId]]
  );

  const { mutate: deleteChairRoleSlotMut } = useApiSend(
    (p: { id: string; slotId: string }) =>
      deleteFundChairRoleSlot(p.id, p.slotId),
    () => {
      setChairRoleSlotSaving(false);
      setChairRoleSlotError(null);
    },
    (e: Error) => {
      setChairRoleSlotSaving(false);
      setChairRoleSlotError(getApiErrorMessage(e));
    },
    [['fund-detail', fundId]]
  );

  const { mutate: addEmailContactMut } = useApiSend(
    (p: { id: string; body: CreateFundEmailContactRequest }) =>
      createFundEmailContact(p.id, p.body),
    () => {
      setEmailSaving(false);
      setEmailError(null);
    },
    (e: Error) => {
      setEmailSaving(false);
      setEmailError(getApiErrorMessage(e));
    },
    [['fund-detail', fundId]]
  );

  const { mutate: patchEmailContactMut } = useApiSend(
    (p: { id: string; memberId: string; body: PatchFundEmailContactRequest }) =>
      patchFundEmailContact(p.id, p.memberId, p.body),
    () => {
      setEmailSaving(false);
      setEmailError(null);
    },
    (e: Error) => {
      setEmailSaving(false);
      setEmailError(getApiErrorMessage(e));
    },
    [['fund-detail', fundId]]
  );

  const { mutate: deleteEmailContactMut } = useApiSend(
    (p: { id: string; memberId: string }) =>
      deleteFundEmailContact(p.id, p.memberId),
    () => {
      setEmailSaving(false);
      setEmailError(null);
    },
    (e: Error) => {
      setEmailSaving(false);
      setEmailError(getApiErrorMessage(e));
    },
    [['fund-detail', fundId]]
  );

  const { mutateAsync: createEmailRoleSlotMut } = useApiSend(
    (p: { id: string; body: CreateFundRoleSlotRequest }) =>
      createFundEmailRoleSlot(p.id, p.body),
    () => {
      setEmailRoleSlotSaving(false);
      setEmailRoleSlotError(null);
    },
    (e: Error) => {
      setEmailRoleSlotSaving(false);
      setEmailRoleSlotError(getApiErrorMessage(e));
    },
    [['fund-detail', fundId]]
  );

  const { mutateAsync: patchEmailRoleSlotMut } = useApiSend(
    (p: { id: string; slotId: string; body: PatchFundRoleSlotRequest }) =>
      patchFundEmailRoleSlot(p.id, p.slotId, p.body),
    () => {
      setEmailRoleSlotSaving(false);
      setEmailRoleSlotError(null);
    },
    (e: Error) => {
      setEmailRoleSlotSaving(false);
      setEmailRoleSlotError(getApiErrorMessage(e));
    },
    [['fund-detail', fundId]]
  );

  const { mutate: deleteEmailRoleSlotMut } = useApiSend(
    (p: { id: string; slotId: string }) =>
      deleteFundEmailRoleSlot(p.id, p.slotId),
    () => {
      setEmailRoleSlotSaving(false);
      setEmailRoleSlotError(null);
    },
    (e: Error) => {
      setEmailRoleSlotSaving(false);
      setEmailRoleSlotError(getApiErrorMessage(e));
    },
    [['fund-detail', fundId]]
  );

  const canManage = data?.can_manage === true;
  const canManageAttachments = data?.can_manage_attachments === true;
  const canManageStatementData = data?.can_manage_statement_data === true;

  useEffect(() => {
    if (detailTab === FUND_STATEMENT_TAB && !canManageStatementData) {
      setDetailTab('information');
    }
  }, [detailTab, canManageStatementData]);
  const showNamedAmounts = data?.uses_named_amounts === true;
  const isPrimaryFund = data?.is_primary === 1;
  const showCategoryField = !isPrimaryFund || Boolean(categoryId);
  const showFundTypeFields = !isPrimaryFund || Boolean(categoryId);

  const typeOptions = useMemo(() => {
    const all = data?.fund_type_options ?? [];
    if (!categoryId) return [];
    return all.filter((t) => t.category_id === categoryId);
  }, [data?.fund_type_options, categoryId]);

  const subtypeOptions = useMemo(() => {
    const all = data?.fund_subtype_options ?? [];
    if (!typeId) return [];
    return all.filter((s) => s.type_id === typeId);
  }, [data?.fund_subtype_options, typeId]);

  const subtypeRequired = subtypeOptions.length > 0;

  const usesTypeAllocations = useMemo(
    () => categoryId === FUND_CATEGORY_TYPE_ALLOCATION_ID,
    [categoryId]
  );

  const activeEmailMembers = useMemo(() => {
    const today = dayjs().startOf('day');
    return (data?.email_members ?? []).filter(
      (m) => !m.end_date || !dayjs(m.end_date).startOf('day').isBefore(today)
    );
  }, [data?.email_members]);

  const emailMemberRoleName = data?.email_roles?.[0]?.name || 'Member';
  const emailMemberRoleId = data?.email_roles?.[0]?.id || '';

  const handleSaveFund = () => {
    if (!fundId) return;
    if (usesTypeAllocations) {
      if (assignedTypes.length < 1) {
        setFundError('At least one type allocation is required.');
        return;
      }
      if (transferAllocationMode === TRANSFER_ALLOCATION_MODE_NORMAL) {
        const percentRows = assignedTypes.filter((row) => {
          const n = parseFloat(row.percentage);
          return Number.isFinite(n) && n > 0;
        });
        if (percentRows.length > 0) {
          const total = percentRows.reduce((sum, row) => {
            const n = parseFloat(row.percentage);
            return sum + (Number.isFinite(n) ? n : 0);
          }, 0);
          const totalRounded =
            Math.round((total + Number.EPSILON) * 10000) / 10000;
          if (Math.abs(totalRounded - 100) >= 0.00005) {
            setFundError(
              'Type allocation percentages must sum to 100 (4 decimal places).'
            );
            return;
          }
        }
      }
      const seenTypeIds = new Set<string>();
      const seenOtherTransferNames = new Set<string>();
      for (let i = 0; i < assignedTypes.length; i += 1) {
        const row = assignedTypes[i];
        if (!row.type_id) {
          setFundError(`Type is required for allocation row ${i + 1}.`);
          return;
        }
        const rowSubtypes = (data?.fund_subtype_options ?? []).filter(
          (s) => s.type_id === row.type_id
        );
        if (rowSubtypes.length > 0 && !row.subtype_id) {
          setFundError(`Subtype is required for allocation row ${i + 1}.`);
          return;
        }
        const special = fundSubtypeIsSpecialRecognition(row.subtype_id);
        const pct = parseFloat(row.percentage);
        const hasPct = Number.isFinite(pct) && pct > 0;
        const amt = parseFloat(row.amount);
        const hasAmt =
          !special &&
          row.amount.trim() !== '' &&
          Number.isFinite(amt) &&
          amt > 0;
        if (special && !hasPct) {
          setFundError(
            `Percentage is required for special recognition on allocation row ${i + 1}.`
          );
          return;
        }
        if (special) {
          const countRaw = row.special_recognition_count.trim() || '1';
          const count = Number(countRaw);
          if (!Number.isInteger(count) || count < 1 || count > 20) {
            setFundError(
              `Recognition count must be a whole number from 1 to 20 on allocation row ${i + 1}.`
            );
            return;
          }
        }
        if (hasPct && hasAmt) {
          setFundError(
            `Allocation row ${i + 1} cannot have both percentage and amount.`
          );
          return;
        }
        if (!hasPct && !hasAmt) {
          setFundError(
            `Allocation row ${i + 1} requires either a percentage or an amount.`
          );
          return;
        }
        if (hasPct && pct > 100) {
          setFundError(
            `Percentage must be between 0 and 100 for allocation row ${i + 1}.`
          );
          return;
        }
        if (
          fundTypeRequiresCustomName(row.type_id) &&
          !row.other_transfer_name.trim()
        ) {
          setFundError(`Custom name is required for allocation row ${i + 1}.`);
          return;
        }
        if (!fundTypeAllowsMultipleRows(row.type_id)) {
          if (seenTypeIds.has(row.type_id)) {
            setFundError(
              `Each type can only be used once. Duplicate type on row ${i + 1}.`
            );
            return;
          }
          seenTypeIds.add(row.type_id);
        }
        if (fundTypeAllowsMultipleRows(row.type_id)) {
          const nameKey = row.other_transfer_name.trim().toLowerCase();
          if (seenOtherTransferNames.has(nameKey)) {
            setFundError(
              `Custom name must be unique for Other Transfers. Duplicate name on row ${i + 1}.`
            );
            return;
          }
          seenOtherTransferNames.add(nameKey);
        }
        if (
          fundSubtypeRequiresNamedLimit(row.subtype_id) &&
          row.named_limit.trim()
        ) {
          const trimmedLimit = row.named_limit.trim();
          if (!namedLimitPattern.test(trimmedLimit)) {
            setFundError(
              `Named scholarship limit must be between 1 and 10 for allocation row ${i + 1}.`
            );
            return;
          }
          const limit = Number(trimmedLimit);
          if (!Number.isInteger(limit) || limit < 1 || limit > 10) {
            setFundError(
              `Named scholarship limit must be between 1 and 10 for allocation row ${i + 1}.`
            );
            return;
          }
        }
      }
    } else if (subtypeRequired && !subtypeId) {
      setFundError('Subtype is required for the selected type.');
      return;
    }

    if (optionDirty && yearlyAllocation.trim()) {
      const yearly = parseFloat(yearlyAllocation);
      if (!Number.isFinite(yearly) || yearly <= 0) {
        setFundError(
          'Pre-determined scholarship amount must be greater than zero.'
        );
        return;
      }
    }

    const assignedPayload: FundAssignedTypeInput[] | undefined =
      usesTypeAllocations
        ? assignedTypes.map((row) => {
            const special = fundSubtypeIsSpecialRecognition(row.subtype_id);
            const pct = parseFloat(row.percentage);
            const hasPct = Number.isFinite(pct) && pct > 0;
            const amt = parseFloat(row.amount);
            const hasAmt =
              !special &&
              row.amount.trim() !== '' &&
              Number.isFinite(amt) &&
              amt > 0;
            const count = Number(row.special_recognition_count.trim() || '1');
            return {
              type_id: row.type_id,
              subtype_id: row.subtype_id || null,
              percentage: hasPct ? pct : 0,
              other_transfer_name: fundTypeRequiresCustomName(row.type_id)
                ? row.other_transfer_name.trim()
                : undefined,
              amount: hasAmt ? amt : undefined,
              named_limit:
                fundSubtypeRequiresNamedLimit(row.subtype_id) &&
                namedLimitPattern.test(row.named_limit.trim())
                  ? Number(row.named_limit.trim())
                  : undefined,
              special_recognition_count:
                special && Number.isInteger(count) ? count : undefined,
            };
          })
        : undefined;

    let fundOptionPayload: PatchFundOptionInput | undefined;
    if (optionDirty && distributionOption) {
      fundOptionPayload = {
        option: distributionOption,
        description: optionDescription.trim() || undefined,
      };
      if (distributionOption === FUND_OPTION_B && yearlyAllocation.trim()) {
        fundOptionPayload.yearly_allocation = parseFloat(yearlyAllocation);
      }
    }

    const principalParsed = principalAmount.trim()
      ? parseFloat(principalAmount)
      : null;
    const body: PatchFundRequest = {
      name,
      short_name: shortName,
      account_id: accountId,
      description,
      can_donate: canDonate ? 1 : 0,
      // Empty string clears; JSON null is skipped by the Go *string binding.
      category_id: categoryId || '',
      type_id: usesTypeAllocations ? null : typeId || null,
      subtype_id: usesTypeAllocations
        ? null
        : subtypeRequired
          ? subtypeId || null
          : null,
      assigned_types: usesTypeAllocations
        ? (assignedPayload ?? null)
        : undefined,
      transfer_allocation_mode: usesTypeAllocations
        ? transferAllocationMode
        : undefined,
      status_id: statusId || null,
      // Empty string clears; JSON null is skipped by the Go *string binding.
      spd_chapter_id: spdChapterId || '',
      local_chapter_id: localChapterId || '',
      established_by: establishedBy,
      // Empty string clears the date; JSON null is skipped by the Go *string binding.
      established_date: establishedDate
        ? establishedDate.format('YYYY-MM-DD')
        : '',
      opt_out: optOut ? 1 : 0,
      selection_committee: selectionCommittee,
      transfer_letter: transferLetter,
      statement_paragraph: statementParagraph,
      activity_letter: activityLetter,
      fund_use: fundUse,
      principal_amount:
        principalParsed != null && Number.isFinite(principalParsed)
          ? principalParsed
          : 0,
      as_of_date: (asOfDate?.isValid() ? asOfDate : dayjs()).format(
        'YYYY-MM-DD'
      ),
      fund_option: fundOptionPayload,
    };
    patchFundMut({ id: fundId, body });
  };

  const fundFieldDisplay = (value?: string) => {
    const trimmed = value?.trim();
    return trimmed ? trimmed : '—';
  };

  const handleAddNote = () => {
    if (!fundId || !newNote.trim()) return;
    addNoteMut({ id: fundId, note: newNote.trim() });
  };

  const openEditNote = (noteId: string, text: string) => {
    setEditNoteId(noteId);
    setEditNoteText(text);
    setNoteDialogOpen(true);
  };

  const handleSaveNote = () => {
    if (!fundId || !editNoteId) return;
    patchNoteMut({ id: fundId, noteId: editNoteId, note: editNoteText.trim() });
  };

  const handleDeleteNote = (noteId: string) => {
    if (!fundId) return;
    if (!window.confirm('Delete this note?')) return;
    deleteNoteMut({ id: fundId, noteId });
  };

  const runChairAppointment = (
    pending: {
      userId: string;
      roleId: string;
      startDate: string;
      endDate?: string | null;
      memberId?: string;
    },
    applyToOtherFunds: boolean
  ) => {
    if (!fundId) return;
    setPositionSaving(true);
    setPositionError(null);
    if (pending.memberId) {
      replacePositionMut({
        id: fundId,
        memberId: pending.memberId,
        body: {
          user_id: pending.userId,
          start_date: pending.startDate,
          end_date: pending.endDate || undefined,
          apply_to_other_funds: applyToOtherFunds,
        },
      });
    } else {
      addPositionMut({
        id: fundId,
        body: {
          user_id: pending.userId,
          role_id: pending.roleId,
          start_date: pending.startDate,
          end_date: pending.endDate || undefined,
          apply_to_other_funds: applyToOtherFunds,
        },
      });
    }
  };

  const onSavePosition = (save: PositionSaveData) => {
    if (!fundId) return;
    setPositionSaving(true);
    setPositionError(null);
    if (save.action === 'removeRecord') {
      deletePositionMut({ id: fundId, memberId: save.id });
      return;
    }
    if (save.action === 'editDates') {
      const body: PatchFundPositionRequest = {
        start_date: save.startDate || undefined,
        end_date:
          save.endDate === null || save.endDate === undefined
            ? ''
            : save.endDate,
      };
      if (save.positionId) {
        body.role_id = save.positionId;
      }
      patchPositionMut({ id: fundId, memberId: save.id, body });
      return;
    }
    if (save.action === 'newAppointment') {
      if (!save.userId || !save.startDate || !save.positionId) {
        setPositionSaving(false);
        setPositionError('Member, role, and start date are required.');
        return;
      }
      const pending = {
        userId: save.userId,
        roleId: save.positionId,
        startDate: save.startDate,
        endDate: save.endDate,
        memberId: save.id || undefined,
      };
      // Empty slot: appoint on this fund only (no multi-fund replace prompt).
      if (!save.id) {
        runChairAppointment(pending, false);
        return;
      }
      // Replace: look up other funds for the outgoing chair (Betty), not the incoming (Sue).
      if (!save.incumbentUserId) {
        runChairAppointment(pending, false);
        return;
      }
      getFundChairOtherFunds(fundId, save.incumbentUserId, save.positionId)
        .then(({ other_funds }) => {
          if (other_funds.length === 0) {
            runChairAppointment(pending, false);
          } else {
            setPositionSaving(false);
            setChairConflict({
              funds: other_funds,
              pending,
            });
          }
        })
        .catch((e: unknown) => {
          setPositionSaving(false);
          setPositionError(getApiErrorMessage(e));
        });
    }
  };

  const onSaveEmailContact = (save: PositionSaveData) => {
    if (!fundId) return;
    setEmailSaving(true);
    setEmailError(null);
    if (save.action === 'removeRecord') {
      deleteEmailContactMut({ id: fundId, memberId: save.id });
      return;
    }
    if (save.action === 'editDates') {
      const body: PatchFundEmailContactRequest = {
        start_date: save.startDate || undefined,
        end_date:
          save.endDate === null || save.endDate === undefined
            ? ''
            : save.endDate,
      };
      if (save.userId) {
        body.user_id = save.userId;
      }
      patchEmailContactMut({ id: fundId, memberId: save.id, body });
      return;
    }
    if (save.action === 'newAppointment') {
      if (!save.userId || !save.startDate) {
        setEmailSaving(false);
        setEmailError('Member and start date are required.');
        return;
      }
      addEmailContactMut({
        id: fundId,
        body: {
          user_id: save.userId,
          start_date: save.startDate,
          end_date: save.endDate || undefined,
        },
      });
    }
  };

  if (!fundId) {
    return null;
  }

  if (isLoading) {
    return (
      <Grid container paddingLeft={2} paddingRight={2} sx={{ pt: 3 }}>
        <Typography>Loading…</Typography>
      </Grid>
    );
  }

  if (isError || !data) {
    return (
      <Grid container paddingLeft={2} paddingRight={2} sx={{ pt: 3 }}>
        <Grid item xs={12}>
          <AlertItem
            severity="error"
            description={getApiErrorMessage(loadError)}
            onClose={() => {}}
            keepOpen
          />
        </Grid>
        <Grid item xs={12}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => requestNavigateAway('/finance/funds')}
          >
            Back to funds
          </Button>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container paddingLeft={2} paddingRight={2} spacing={2} sx={{ pb: 4 }}>
      <Grid item xs={12}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => requestNavigateAway('/finance/funds')}
          sx={{ mb: 1 }}
        >
          Back to funds
        </Button>
      </Grid>

      <Grid item xs={12}>
        <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader
            title={data.name || 'Fund'}
            subheader={data.account_id || data.id}
          />
          <CardContent>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {fundFieldDisplay(editingFund ? description : data.description)}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Tabs
          value={detailTab}
          onChange={(_, v: FundDetailTab) => requestDetailTabChange(v)}
          sx={{ mb: 1 }}
        >
          <Tab value="information" label="Information" />
          <Tab value="positions" label="Positions" />
          <Tab value="notes" label="Notes" />
          <Tab value="attachments" label="Attachments" />
          {canManageStatementData && (
            <Tab value={FUND_STATEMENT_TAB} label="Statement Data" />
          )}
          <Tab value={FUND_HISTORY_TAB} label="History" />
        </Tabs>
      </Grid>

      {detailTab === 'information' && (
        <>
          <Grid item xs={12}>
            <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
              <CardHeader
                title="Fund"
                subheader={data.account_id || data.id}
                action={
                  canManage ? (
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        flexWrap: 'wrap',
                        justifyContent: 'flex-end',
                        alignItems: 'flex-start',
                      }}
                    >
                      {editingFund ? (
                        <>
                          <DatePicker
                            label="As of date"
                            value={asOfDate}
                            onChange={(v) => {
                              if (v) setAsOfDate(v);
                            }}
                            slotProps={{
                              textField: {
                                size: 'small',
                                required: true,
                                sx: { minWidth: 160 },
                                helperText:
                                  'History: principal, status, category, type, subtype',
                              },
                            }}
                          />
                          <Button
                            variant="contained"
                            color="warning"
                            size="small"
                            startIcon={<Cancel />}
                            onClick={() => {
                              if (
                                hasUnsavedFundEdits &&
                                !window.confirm(
                                  'You have unsaved changes. Discard them?'
                                )
                              ) {
                                return;
                              }
                              discardFundEdits();
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            startIcon={<Save />}
                            onClick={handleSaveFund}
                          >
                            Save
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<Edit />}
                          onClick={() => setEditingFund(true)}
                        >
                          Edit
                        </Button>
                      )}
                    </Box>
                  ) : undefined
                }
              />
              <CardContent>
                {fundError && (
                  <Box sx={{ mb: 2 }}>
                    <AlertItem
                      severity="error"
                      description={fundError}
                      onClose={() => setFundError(null)}
                      keepOpen
                    />
                  </Box>
                )}
                <Grid container spacing={2}>
                  {editingFund ? (
                    <>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          fullWidth
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Short name"
                          value={shortName}
                          onChange={(e) => setShortName(e.target.value)}
                          fullWidth
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Account"
                          value={accountId}
                          onChange={(e) => setAccountId(e.target.value)}
                          fullWidth
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Principal amount"
                          type="number"
                          value={principalAmount}
                          onChange={(e) => setPrincipalAmount(e.target.value)}
                          fullWidth
                          size="small"
                          inputProps={{ min: 0, step: '0.01' }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Distribution option</InputLabel>
                          <Select
                            label="Distribution option"
                            value={distributionOption}
                            onChange={(e) => {
                              const next = String(e.target.value);
                              setDistributionOption(next);
                              setOptionDirty(true);
                              if (next !== FUND_OPTION_B) {
                                setYearlyAllocation('');
                              }
                            }}
                          >
                            <MenuItem value="">
                              <em>Select option</em>
                            </MenuItem>
                            {(
                              Object.keys(
                                FUND_OPTION_LABELS
                              ) as FundOptionCode[]
                            ).map((code) => (
                              <MenuItem key={code} value={code}>
                                {FUND_OPTION_LABELS[code]}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      {distributionOption === FUND_OPTION_B && (
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Pre-determined scholarship amount"
                            type="number"
                            value={yearlyAllocation}
                            onChange={(e) => {
                              setYearlyAllocation(e.target.value);
                              setOptionDirty(true);
                            }}
                            fullWidth
                            size="small"
                            inputProps={{ min: 0.01, step: '0.01' }}
                          />
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <TextField
                          label="Option description"
                          value={optionDescription}
                          onChange={(e) => {
                            setOptionDescription(e.target.value);
                            setOptionDirty(true);
                          }}
                          fullWidth
                          size="small"
                          inputProps={{ maxLength: 255 }}
                          helperText={
                            distributionOption === FUND_OPTION_CUSTOM
                              ? 'Optional note describing custom logic'
                              : undefined
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Description"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          fullWidth
                          size="small"
                          multiline
                          minRows={2}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={canDonate}
                              onChange={(e) => setCanDonate(e.target.checked)}
                            />
                          }
                          label="Can donate"
                        />
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <b>Name:</b> {data.name || '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <b>Short name:</b> {data.short_name || '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <b>Account:</b> {data.account_id || '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <b>Principal amount:</b>{' '}
                          {data.principal_amount != null &&
                          data.principal_amount > 0
                            ? `$${formatAllocationAmount(data.principal_amount)}`
                            : '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="body2"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <b>Distribution option:</b>{' '}
                          {data.current_fund_option
                            ? fundOptionLabel(data.current_fund_option.option)
                            : '—'}
                          {data.current_fund_option?.option ===
                            FUND_OPTION_CUSTOM &&
                            !data.current_fund_option
                              .custom_rule_configured && (
                              <Tooltip title="Custom rule not configured">
                                <WarningAmber
                                  color="error"
                                  fontSize="small"
                                  sx={{ ml: 0.5 }}
                                />
                              </Tooltip>
                            )}
                        </Typography>
                      </Grid>
                      {data.current_fund_option?.option === FUND_OPTION_B && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2">
                            <b>Pre-determined scholarship amount:</b>{' '}
                            {data.current_fund_option.yearly_allocation != null
                              ? `$${formatAllocationAmount(data.current_fund_option.yearly_allocation)}`
                              : '—'}
                          </Typography>
                        </Grid>
                      )}
                      {data.current_fund_option?.description && (
                        <Grid item xs={12}>
                          <Typography variant="body2">
                            <b>Option description:</b>{' '}
                            {data.current_fund_option.description}
                          </Typography>
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <Typography variant="body2">
                          <b>Can donate:</b>{' '}
                          {data.can_donate === 1 ? 'Yes' : 'No'}
                        </Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
              <CardHeader title="Chapter & classification" />
              <CardContent>
                <Grid container spacing={2}>
                  {editingFund ? (
                    <>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Status</InputLabel>
                          <Select
                            label="Status"
                            value={statusId}
                            onChange={(e) =>
                              setStatusId(String(e.target.value))
                            }
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {(data.fund_status_options ?? []).map((s) => (
                              <MenuItem key={s.id} value={s.id}>
                                {s.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>SPD chapter</InputLabel>
                          <Select
                            label="SPD chapter"
                            value={spdChapterId}
                            onChange={(e) => {
                              setSpdChapterId(String(e.target.value));
                              setLocalChapterId('');
                            }}
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {spdChapters.map((sp) => (
                              <MenuItem key={sp.id} value={sp.id}>
                                {spdLabel(sp)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <ChapterSelector
                          chapter={localChapterId}
                          spd={spdChapterId || null}
                          all={!spdChapterId}
                          keyId={`fund-detail-${fundId}-lc`}
                          label="Local chapter"
                          handleChapterSelect={(chapterId) =>
                            setLocalChapterId(chapterId || '')
                          }
                        />
                      </Grid>
                      {showCategoryField && (
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Category</InputLabel>
                            <Select
                              label="Category"
                              value={categoryId}
                              onChange={(e) => {
                                const next = String(e.target.value);
                                setCategoryId(next);
                                const types = (
                                  data.fund_type_options ?? []
                                ).filter((t) => t.category_id === next);
                                if (next === FUND_CATEGORY_TYPE_ALLOCATION_ID) {
                                  setTypeId('');
                                  setSubtypeId('');
                                  if (assignedTypes.length === 0) {
                                    setAssignedTypes([
                                      {
                                        type_id: '',
                                        subtype_id: '',
                                        percentage: '100',
                                        other_transfer_name: '',
                                        amount: '',
                                        named_limit: '',
                                        special_recognition_count: '',
                                      },
                                    ]);
                                  }
                                } else if (
                                  !types.some((t) => t.id === typeId)
                                ) {
                                  setTypeId('');
                                  setSubtypeId('');
                                  setAssignedTypes([]);
                                } else {
                                  const subtypes = (
                                    data.fund_subtype_options ?? []
                                  ).filter((s) => s.type_id === typeId);
                                  if (
                                    !subtypes.some((s) => s.id === subtypeId)
                                  ) {
                                    setSubtypeId('');
                                  }
                                  setAssignedTypes([]);
                                }
                              }}
                            >
                              <MenuItem value="">
                                <em>None</em>
                              </MenuItem>
                              {data.fund_category_options.map((c) => (
                                <MenuItem key={c.id} value={c.id}>
                                  {c.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      )}
                      {showFundTypeFields &&
                        (usesTypeAllocations ? (
                          <>
                            <Grid item xs={12} md={6}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Allocation mode</InputLabel>
                                <Select
                                  label="Allocation mode"
                                  value={transferAllocationMode}
                                  onChange={(e) =>
                                    setTransferAllocationMode(
                                      String(e.target.value) ===
                                        TRANSFER_ALLOCATION_MODE_ORDER
                                        ? TRANSFER_ALLOCATION_MODE_ORDER
                                        : TRANSFER_ALLOCATION_MODE_NORMAL
                                    )
                                  }
                                >
                                  <MenuItem
                                    value={TRANSFER_ALLOCATION_MODE_NORMAL}
                                  >
                                    Normal (fixed first, % of remainder)
                                  </MenuItem>
                                  <MenuItem
                                    value={TRANSFER_ALLOCATION_MODE_ORDER}
                                  >
                                    Order (apply rows top-to-bottom)
                                  </MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            <FundTypeAllocationEditor
                              rows={assignedTypes}
                              categoryId={categoryId}
                              mode={transferAllocationMode}
                              typeOptions={data.fund_type_options ?? []}
                              subtypeOptions={data.fund_subtype_options ?? []}
                              onChange={setAssignedTypes}
                            />
                          </>
                        ) : (
                          <>
                            <Grid item xs={12} md={6}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Type</InputLabel>
                                <Select
                                  label="Type"
                                  value={typeId}
                                  onChange={(e) => {
                                    const next = String(e.target.value);
                                    setTypeId(next);
                                    const subtypes = (
                                      data.fund_subtype_options ?? []
                                    ).filter((s) => s.type_id === next);
                                    if (
                                      !subtypes.some((s) => s.id === subtypeId)
                                    ) {
                                      setSubtypeId('');
                                    }
                                  }}
                                  disabled={!categoryId}
                                >
                                  <MenuItem value="">
                                    <em>None</em>
                                  </MenuItem>
                                  {typeOptions.map((t) => (
                                    <MenuItem key={t.id} value={t.id}>
                                      {t.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            {subtypeOptions.length > 0 && (
                              <Grid item xs={12} md={6}>
                                <FormControl fullWidth size="small" required>
                                  <InputLabel>Subtype</InputLabel>
                                  <Select
                                    label="Subtype"
                                    value={subtypeId}
                                    onChange={(e) =>
                                      setSubtypeId(String(e.target.value))
                                    }
                                  >
                                    <MenuItem value="">
                                      <em>Select subtype</em>
                                    </MenuItem>
                                    {subtypeOptions.map((s) => (
                                      <MenuItem key={s.id} value={s.id}>
                                        {s.name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>
                            )}
                          </>
                        ))}
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Established by"
                          value={establishedBy}
                          onChange={(e) => setEstablishedBy(e.target.value)}
                          fullWidth
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <DatePicker
                          label="Established date"
                          value={establishedDate}
                          onChange={(v) => setEstablishedDate(v)}
                          slotProps={{
                            textField: { fullWidth: true, size: 'small' },
                          }}
                        />
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <b>Status:</b>{' '}
                          {data.status_name || data.status_id || '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <b>SPD chapter:</b>{' '}
                          {data.spd_chapter_name || data.spd_chapter_id || '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <b>Local chapter:</b>{' '}
                          {data.local_chapter_name ||
                            data.local_chapter_id ||
                            '—'}
                        </Typography>
                      </Grid>
                      {(!isPrimaryFund || data.category_id) && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2">
                            <b>Category:</b> {data.category_name || '—'}
                          </Typography>
                        </Grid>
                      )}
                      {data.uses_type_allocations ? (
                        <Grid item xs={12}>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <b>Allocation mode:</b>{' '}
                            {data.transfer_allocation_mode ===
                            TRANSFER_ALLOCATION_MODE_ORDER
                              ? 'Order (apply rows top-to-bottom)'
                              : 'Normal (fixed first, % of remainder)'}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <b>Type allocations:</b>
                          </Typography>
                          {(data.assigned_types ?? []).length > 0 ? (
                            (data.assigned_types ?? []).map((row, i) => (
                              <Typography
                                key={
                                  row.id ||
                                  `${row.type_id}-${row.subtype_id ?? ''}-${row.other_transfer_name ?? ''}-${row.percentage}-${i}`
                                }
                                variant="body2"
                              >
                                {data.transfer_allocation_mode ===
                                TRANSFER_ALLOCATION_MODE_ORDER
                                  ? `${i + 1}. `
                                  : ''}
                                {row.type_name || row.type_id}
                                {row.other_transfer_name
                                  ? ` — ${row.other_transfer_name}`
                                  : ''}
                                {row.subtype_name
                                  ? ` (${row.subtype_name})`
                                  : ''}
                                {row.amount != null && row.amount > 0
                                  ? ` — $${formatAllocationAmount(row.amount)}`
                                  : row.percentage > 0
                                    ? ` — ${row.percentage}%`
                                    : ''}
                                {row.named_limit != null && row.named_limit > 0
                                  ? ` — limit ${row.named_limit}`
                                  : ''}
                                {fundSubtypeIsSpecialRecognition(
                                  row.subtype_id || ''
                                ) &&
                                row.special_recognition_count != null &&
                                row.special_recognition_count > 1
                                  ? ` — ${row.special_recognition_count} recognitions`
                                  : ''}
                              </Typography>
                            ))
                          ) : (
                            <Typography variant="body2">—</Typography>
                          )}
                        </Grid>
                      ) : (
                        <>
                          <Grid item xs={12} md={6}>
                            <Typography variant="body2">
                              <b>Type:</b> {data.type_name || '—'}
                            </Typography>
                          </Grid>
                          {data.subtype_name && (
                            <Grid item xs={12} md={6}>
                              <Typography variant="body2">
                                <b>Subtype:</b> {data.subtype_name}
                              </Typography>
                            </Grid>
                          )}
                        </>
                      )}
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <b>Established by:</b> {data.established_by || '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <b>Established date:</b>{' '}
                          {data.established_date || '—'}
                        </Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {showNamedAmounts && (
            <Grid item xs={12}>
              <FundNamedAmountsSection
                fundId={fundId}
                categoryId={data.category_id || ''}
                namedAmounts={data.named_amounts ?? []}
                typeOptions={data.fund_type_options ?? []}
                canManage={canManage}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
              <CardHeader title="Fund letters" />
              <CardContent>
                <Grid container spacing={2}>
                  {editingFund ? (
                    <>
                      <Grid item xs={12}>
                        <TextField
                          label="Transfer letter"
                          value={transferLetter}
                          onChange={(e) => setTransferLetter(e.target.value)}
                          fullWidth
                          size="small"
                          multiline
                          minRows={4}
                          inputProps={{ maxLength: 2000 }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Statement paragraph"
                          value={statementParagraph}
                          onChange={(e) =>
                            setStatementParagraph(e.target.value)
                          }
                          fullWidth
                          size="small"
                          multiline
                          minRows={4}
                          inputProps={{ maxLength: 2000 }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Activity letter"
                          value={activityLetter}
                          onChange={(e) => setActivityLetter(e.target.value)}
                          fullWidth
                          size="small"
                          multiline
                          minRows={4}
                          inputProps={{ maxLength: 2000 }}
                        />
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid item xs={12}>
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: 'pre-wrap' }}
                        >
                          <b>Transfer letter:</b>{' '}
                          {fundFieldDisplay(data.transfer_letter)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: 'pre-wrap' }}
                        >
                          <b>Statement paragraph:</b>{' '}
                          {fundFieldDisplay(data.statement_paragraph)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: 'pre-wrap' }}
                        >
                          <b>Activity letter:</b>{' '}
                          {fundFieldDisplay(data.activity_letter)}
                        </Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
              <CardHeader title="Fund options" />
              <CardContent>
                <Grid container spacing={2}>
                  {editingFund ? (
                    <>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={optOut}
                              onChange={(e) => setOptOut(e.target.checked)}
                            />
                          }
                          label="Opt out"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Selection committee"
                          value={selectionCommittee}
                          onChange={(e) =>
                            setSelectionCommittee(e.target.value)
                          }
                          fullWidth
                          size="small"
                          inputProps={{ maxLength: 255 }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Fund use"
                          value={fundUse}
                          onChange={(e) => setFundUse(e.target.value)}
                          fullWidth
                          size="small"
                          multiline
                          minRows={4}
                          inputProps={{ maxLength: 2000 }}
                        />
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="body2">
                          <b>Opt out:</b> {data.opt_out === 1 ? 'Yes' : 'No'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: 'pre-wrap' }}
                        >
                          <b>Selection committee:</b>{' '}
                          {fundFieldDisplay(data.selection_committee)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: 'pre-wrap' }}
                        >
                          <b>Fund use:</b> {fundFieldDisplay(data.fund_use)}
                        </Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </>
      )}

      {detailTab === 'positions' && (
        <>
          <Grid item xs={12}>
            <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
              <CardHeader title="Chair / Co-Chair" />
              <CardContent>
                {positionError && (
                  <Box sx={{ mb: 2 }}>
                    <AlertItem
                      severity="error"
                      description={positionError}
                      onClose={() => setPositionError(null)}
                      keepOpen
                    />
                  </Box>
                )}
                {data.chair_roles.map((role) => {
                  const active = data.positions.find(
                    (p) => p.position_id === role.id
                  );
                  return (
                    <PositionDisplay
                      key={role.id}
                      id={active?.id ?? ''}
                      position={role.name}
                      positionId={role.id}
                      committeeId={undefined}
                      name={active?.name}
                      startDate={active?.start_date || null}
                      endDate={active?.end_date ?? null}
                      canEdit={canManage}
                      isSaving={positionSaving}
                      errorSavingText={positionError || undefined}
                      onSave={onSavePosition}
                      userId={active?.user_id}
                      pageFrom="finance-fund-chairs"
                      fundId={fundId}
                      showRoleHistory={canManage}
                      canMessage={false}
                      chairRoleOptions={data.chair_roles}
                    />
                  );
                })}
                {data.chair_roles.length === 0 && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    No chair roles configured.
                  </Typography>
                )}
                {chairRoleSlotError && (
                  <Box sx={{ mb: 2 }}>
                    <AlertItem
                      severity="error"
                      description={chairRoleSlotError}
                      onClose={() => setChairRoleSlotError(null)}
                      keepOpen
                    />
                  </Box>
                )}
                <FundRoleSlotDisplay
                  title="Role-based assignments"
                  slots={data.chair_role_slots ?? []}
                  groupRoleOptions={data.chair_roles}
                  refRoleOptions={data.ref_role_options ?? []}
                  spdChapters={spdChapters}
                  canEdit={canManage}
                  isSaving={chairRoleSlotSaving}
                  onCreate={async (body) => {
                    if (!fundId) return;
                    setChairRoleSlotSaving(true);
                    setChairRoleSlotError(null);
                    await createChairRoleSlotMut({ id: fundId, body });
                  }}
                  onPatch={async (slotId, body) => {
                    if (!fundId) return;
                    setChairRoleSlotSaving(true);
                    setChairRoleSlotError(null);
                    await patchChairRoleSlotMut({ id: fundId, slotId, body });
                  }}
                  onDelete={(slotId) => {
                    if (!fundId) return;
                    setChairRoleSlotSaving(true);
                    setChairRoleSlotError(null);
                    deleteChairRoleSlotMut({ id: fundId, slotId });
                  }}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
              <CardHeader title="Fund Email Contacts" />
              <CardContent>
                {emailError && (
                  <Box sx={{ mb: 2 }}>
                    <AlertItem
                      severity="error"
                      description={emailError}
                      onClose={() => setEmailError(null)}
                      keepOpen
                    />
                  </Box>
                )}
                <Typography variant="subtitle2" gutterBottom>
                  Members
                </Typography>
                {activeEmailMembers.map((member) => (
                  <PositionDisplay
                    key={member.id}
                    id={member.id}
                    position={member.position || emailMemberRoleName}
                    positionId={member.position_id || emailMemberRoleId}
                    committeeId={undefined}
                    name={member.name}
                    startDate={member.start_date || null}
                    endDate={member.end_date ?? null}
                    canEdit={canManage}
                    isSaving={emailSaving}
                    errorSavingText={emailError || undefined}
                    onSave={onSaveEmailContact}
                    userId={member.user_id}
                    pageFrom="finance-fund-email"
                    fundId={fundId}
                    showRoleHistory={false}
                    canMessage={false}
                  />
                ))}
                {activeEmailMembers.length === 0 && !canManage && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    No active email contacts.
                  </Typography>
                )}
                {canManage && (
                  <PositionDisplay
                    id=""
                    position={emailMemberRoleName}
                    positionId={emailMemberRoleId}
                    committeeId={undefined}
                    canEdit={canManage}
                    isSaving={emailSaving}
                    errorSavingText={emailError || undefined}
                    onSave={onSaveEmailContact}
                    pageFrom="finance-fund-email"
                    fundId={fundId}
                    showRoleHistory={false}
                    canMessage={false}
                  />
                )}
                {emailRoleSlotError && (
                  <Box sx={{ mb: 2, mt: 2 }}>
                    <AlertItem
                      severity="error"
                      description={emailRoleSlotError}
                      onClose={() => setEmailRoleSlotError(null)}
                      keepOpen
                    />
                  </Box>
                )}
                <FundRoleSlotDisplay
                  title="Role-based email assignments"
                  slots={data.email_role_slots ?? []}
                  groupRoleOptions={data.email_roles ?? []}
                  refRoleOptions={data.ref_role_options ?? []}
                  spdChapters={spdChapters}
                  canEdit={canManage}
                  isSaving={emailRoleSlotSaving}
                  fixedGroupRoleId={emailMemberRoleId || undefined}
                  onCreate={async (body) => {
                    if (!fundId) return;
                    setEmailRoleSlotSaving(true);
                    setEmailRoleSlotError(null);
                    await createEmailRoleSlotMut({ id: fundId, body });
                  }}
                  onPatch={async (slotId, body) => {
                    if (!fundId) return;
                    setEmailRoleSlotSaving(true);
                    setEmailRoleSlotError(null);
                    await patchEmailRoleSlotMut({ id: fundId, slotId, body });
                  }}
                  onDelete={(slotId) => {
                    if (!fundId) return;
                    setEmailRoleSlotSaving(true);
                    setEmailRoleSlotError(null);
                    deleteEmailRoleSlotMut({ id: fundId, slotId });
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
        </>
      )}

      {detailTab === 'notes' && (
        <Grid item xs={12}>
          <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
            <CardHeader title="Fund notes" />
            <CardContent>
              {noteError && (
                <Box sx={{ mb: 2 }}>
                  <AlertItem
                    severity="error"
                    description={noteError}
                    onClose={() => setNoteError(null)}
                    keepOpen
                  />
                </Box>
              )}
              {data.notes.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No notes yet.
                </Typography>
              )}
              {data.notes.map((n) => (
                <Box key={n.id} sx={{ mb: 2 }}>
                  <Typography variant="body2">{n.note}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {n.created_by_name || n.created_by_id}
                    {n.created_at
                      ? ` · ${dayjs(n.created_at).format('MM/DD/YYYY h:mm A')}`
                      : ''}
                  </Typography>
                  {canManage && (
                    <Box sx={{ mt: 0.5 }}>
                      <IconButton
                        size="small"
                        aria-label="Edit note"
                        onClick={() => openEditNote(n.id, n.note)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label="Delete note"
                        onClick={() => handleDeleteNote(n.id)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                  <Divider sx={{ mt: 1 }} />
                </Box>
              ))}
              {canManage && (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    label="New note"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    fullWidth
                    multiline
                    minRows={2}
                    size="small"
                  />
                  <Button
                    sx={{ mt: 1 }}
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleAddNote}
                    disabled={!newNote.trim()}
                  >
                    Add note
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      )}

      {detailTab === 'attachments' && (
        <Grid item xs={12}>
          <FundAttachmentsTab
            fundId={fundId}
            canManage={canManageAttachments}
          />
        </Grid>
      )}

      {detailTab === FUND_STATEMENT_TAB && canManageStatementData && fundId && (
        <Grid item xs={12}>
          <FundStatementDataTab
            fundId={fundId}
            canManage={canManageStatementData}
          />
        </Grid>
      )}

      {detailTab === FUND_HISTORY_TAB && (
        <Grid item xs={12}>
          <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
            <CardHeader title="History" />
            <CardContent>
              {historyLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={28} />
                </Box>
              )}
              {historyIsError && (
                <AlertItem
                  severity="error"
                  description={
                    getApiErrorMessage(historyError) ||
                    'Failed to load history.'
                  }
                  keepOpen
                />
              )}
              {!historyLoading &&
                !historyIsError &&
                (historyData?.items?.length ?? 0) === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No history recorded yet.
                  </Typography>
                )}
              {!historyLoading &&
                !historyIsError &&
                (historyData?.items ?? []).map((item, idx) => (
                  <Box
                    key={`${item.kind}-${item.sort_date}-${item.field_name ?? item.summary ?? idx}-${idx}`}
                    sx={{ mb: 2 }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                        mb: 0.5,
                      }}
                    >
                      <Chip
                        size="small"
                        label={fundHistoryKindLabel(item.kind)}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {item.kind === 'field_change'
                          ? item.as_of_date || item.sort_date
                          : fundHistoryRangeLabel(
                              item.start_date || item.sort_date,
                              item.end_date
                            )}
                      </Typography>
                    </Box>
                    {item.kind === 'field_change' ? (
                      <>
                        <Typography variant="body2">
                          <b>{item.field_label || item.field_name}</b>:{' '}
                          {fundHistoryDisplayValue(
                            item.old_label,
                            item.old_value
                          )}{' '}
                          →{' '}
                          {fundHistoryDisplayValue(
                            item.new_label,
                            item.new_value
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.created_by_name || item.created_by_id || '—'}
                          {item.created_at
                            ? ` · recorded ${dayjs(item.created_at).format('MM/DD/YYYY h:mm A')}`
                            : ''}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2">
                        {item.summary || '—'}
                      </Typography>
                    )}
                    <Divider sx={{ mt: 1 }} />
                  </Box>
                ))}
            </CardContent>
          </Card>
        </Grid>
      )}

      <Dialog
        open={chairConflict !== null}
        onClose={() => setChairConflict(null)}
      >
        <DialogTitle>Same chair role on other funds</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            The person you are replacing holds the same chair role on the
            following funds. Replace them with this appointment on this fund
            only, or on all listed funds?
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {chairConflict?.funds.map((f) => (
              <Typography component="li" variant="body2" key={f.fund_id}>
                {f.fund_name || f.fund_id}
                {f.role_name ? ` (${f.role_name})` : ''}
              </Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChairConflict(null)}>Cancel</Button>
          <Button
            onClick={() => {
              if (chairConflict) {
                runChairAppointment(chairConflict.pending, false);
                setChairConflict(null);
              }
            }}
          >
            This fund only
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (chairConflict) {
                runChairAppointment(chairConflict.pending, true);
                setChairConflict(null);
              }
            }}
          >
            All funds
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={noteDialogOpen}
        onClose={() => setNoteDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit note</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Note"
            fullWidth
            multiline
            minRows={3}
            value={editNoteText}
            onChange={(e) => setEditNoteText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveNote}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={unsavedDialogOpen} onClose={handleUnsavedTabStay}>
        <DialogTitle>Unsaved changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved fund changes. Discard them before switching tabs?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUnsavedTabStay}>Stay</Button>
          <Button onClick={handleUnsavedTabDiscard} color="warning">
            Discard and switch
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={leaveDialogOpen} onClose={handleLeaveStay}>
        <DialogTitle>Unsaved changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved fund changes. Leave without saving?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLeaveStay}>Stay</Button>
          <Button onClick={handleLeaveDiscard} color="warning">
            Leave anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default FundDetail;
